import React from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';

export type AvatarSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl';

interface AvatarProps {
  name?: string;
  src?: string;
  size?: AvatarSize;
  color?: string; // explicit background color; sets text to white
  style?: ViewStyle;
}

export default function Avatar({
  name = '?',
  src,
  size = 'default',
  color,
  style,
}: AvatarProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;

  const sizeMap: Record<AvatarSize, number> = {
    xs:      18,
    sm:      24,
    default: 32,
    lg:      44,
    xl:      56,
  };

  const fontSizeMap: Record<AvatarSize, number> = {
    xs:       9,
    sm:      10,
    default: fontSize.sm,
    lg:      fontSize.lg,
    xl:      fontSize.xl,
  };

  const dim = sizeMap[size];
  const fSize = fontSizeMap[size];

  // Derive initials: first letter of each of the first two words
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase() || '?';

  const bgColor = color ?? colors.paper3;
  const textColor = color ? colors.white : colors.ink2;
  const borderColor = color ? colors.transparent : colors.line;

  const containerStyle: ViewStyle = {
    width: dim,
    height: dim,
    borderRadius: dim / 2,
    backgroundColor: bgColor,
    borderWidth: 1,
    borderColor,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  const labelStyle: TextStyle = {
    fontSize: fSize,
    fontWeight: fontWeight.semibold,
    color: textColor,
    fontFamily: theme.font.sans,
    textTransform: 'uppercase',
    includeFontPadding: false,
    lineHeight: fSize * 1.2,
  };

  if (src) {
    return (
      <View style={[containerStyle, style]}>
        <Image
          source={{ uri: src }}
          style={{ width: dim, height: dim, borderRadius: dim / 2 }}
          resizeMode="cover"
          accessibilityLabel={name}
        />
      </View>
    );
  }

  return (
    <View style={[containerStyle, style]}>
      <Text style={labelStyle} numberOfLines={1}>
        {initials}
      </Text>
    </View>
  );
}
